/** Required header names (case-insensitive, extra spaces collapsed). */
export const PQG_RATES_REQUIRED_COLUMN_LABELS = [
    'State',
    'Service Name',
    'Rate Min',
    'Rate Max',
    'Rate Unit',
] as const;

/** Optional columns (not validated as missing if absent). */
export const PQG_RATES_OPTIONAL_COLUMN_LABELS = ['County', 'Year'] as const;

export function normalizePqgRatesColumnName(name: string): string {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

function detectDelimiter(line: string): ',' | ';' {
    const commas = (line.match(/,/g) || []).length;
    const semicolons = (line.match(/;/g) || []).length;
    return semicolons > commas ? ';' : ',';
}

/** Parses one header row; supports quoted fields and comma or semicolon delimiter. */
export function parseDelimitedHeaderLine(line: string, delimiter: ',' | ';'): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            inQuotes = !inQuotes;
        } else if (ch === delimiter && !inQuotes) {
            result.push(current.trim().replace(/^"|"$/g, ''));
            current = '';
        } else {
            current += ch;
        }
    }
    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
}

export function parseCsvHeadersFromText(text: string): string[] {
    const lines = text.split(/\r?\n/);
    const first = lines.find((l) => l.trim().length > 0);
    if (!first) {
        return [];
    }
    const delimiter = detectDelimiter(first);
    return parseDelimitedHeaderLine(first, delimiter);
}

/** Returns display labels of required columns that are missing from the file header row. */
export function missingPqgRatesRequiredColumns(headers: string[]): string[] {
    const normalized = new Set(
        headers.map((h) => normalizePqgRatesColumnName(h)).filter((h) => h.length > 0)
    );
    return PQG_RATES_REQUIRED_COLUMN_LABELS.filter(
        (req) => !normalized.has(normalizePqgRatesColumnName(req))
    );
}

export async function extractSpreadsheetHeaderRow(file: File): Promise<string[]> {
    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) {
        const text = await file.text();
        return parseCsvHeadersFromText(text);
    }
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const XLSX = await import('xlsx');
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) {
            return [];
        }
        const sheet = wb.Sheets[sheetName];
        const ref = sheet['!ref'];
        if (!ref) {
            return [];
        }
        const range = XLSX.utils.decode_range(ref);
        const headers: string[] = [];
        for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r: range.s.r, c });
            const cell = sheet[addr];
            const raw = cell?.w ?? (cell?.v != null ? String(cell.v) : '');
            headers.push(typeof raw === 'string' ? raw.trim() : String(raw).trim());
        }
        return headers;
    }
    throw new Error('Unsupported file type');
}
